import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Select,
  Table,
  Space,
  Popconfirm,
  Alert,
  Input,
  Tabs,
  Tag,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import { useGrades } from "../../../../hooks/useGrades";
import { usePrograms } from "../../../../hooks/usePrograms";
import { useNotification } from "../../../../hooks/useNotification";
import { ClassEntity, ProgramSelected } from "../types";
import {
  createClassroom,
  updateClassroom,
  deleteClassroom,
} from "../../../../services/classrooms/classrooms.service";
import {
  CreateClassroomDto,
  UpdateClassroomDto,
  ClassroomEntity,
  CoordinatorInput,
  SelectionCell,
} from "../../../../services/classrooms/classrooms.types";
import SectionGradeProgramGrid from "../SchoolsTab/SectionGradeProgramGrid";
import { countGenerationDelta } from "../SchoolsTab/SectionGradeProgramGrid.util";
import GenerarClasesModal from "./GenerarClasesModal";
import ClassProgramsDrawer from "./ClassProgramsDrawer";
import ClassTeacherScopeDrawer from "./ClassTeacherScopeDrawer";
import ClassesGuideModal from "./ClassesGuideModal";
import MoveToCampusModal from "../../../MoveToCampusModal";
import { CampusRef } from "../../../../services/schools/campus.types";
import {
  ALL_CAMPUSES,
  campusOptions,
  hasMultipleCampuses,
} from "../UsersTab/UsersTab.utils";

// Temporary client-side ids (a class row not yet persisted) start here — see
// handleAddClass below. "Programas" needs a real backend classroom id.
const TEMP_ID_FLOOR = 1000000;

interface ClassesTabProps {
  classes: ClassEntity[];
  setClasses: React.Dispatch<React.SetStateAction<ClassEntity[]>>;
  editingClassId: number | null;
  setEditingClassId: React.Dispatch<React.SetStateAction<number | null>>;
  editingClass: ClassEntity | null;
  setEditingClass: React.Dispatch<React.SetStateAction<ClassEntity | null>>;
  schoolAbbreviation: string;
  schoolId: number | null;
  /**
   * SCRUM-916: árbol de planteles de la escuela. Con uno solo (o ninguno) esta
   * tab se ve exactamente como antes: sin selector, sin columna, sin "Mover".
   */
  campuses?: CampusRef[];
  groups: string[];
  schoolPrograms: ProgramSelected[];
  // Programs the school is actually LICENSED for (persisted SchoolToProgram),
  // as opposed to `schoolPrograms` which can also include transient,
  // not-yet-generated grid picks. Gates the manual add/edit program selector
  // and the "no programs" enablement/banner — the single-create classroom
  // endpoint performs no license validation, so this is the reachable
  // frontend guard against creating a class for an unlicensed program.
  licensedPrograms: ProgramSelected[];
  // Generation grid state, absorbed from the old Escuela tab — Clases is now
  // the single home for everything class-related.
  generationSelections: SelectionCell[];
  setGenerationSelections: (selections: SelectionCell[]) => void;
  setGroups: (groups: string[]) => void;
  // Rehydrates the grid's selection on edit re-entry from already-created
  // classrooms (grade_id -> program_ids). Empty in creation mode.
  initialGridSelection: Record<number, number[]>;
  // Per-grade groups for that same rehydration. The grid below is keyed on
  // schoolId, so creating the school REMOUNTS it — and groups live in the
  // grid's state. Without this the operator's group names are replaced by a
  // single default "A" per grade at exactly that moment.
  initialGridGroups: Record<number, string[]>;
  // Programs the school is LICENSED for (persisted SchoolToProgram). Narrows
  // the grid's Materia selector — NOT the same as `schoolPrograms`, which is
  // unioned with the live grid selection and would therefore keep whatever
  // was just picked selectable, defeating the restriction.
  schoolProgramIds: number[];
  // Coordinator captured on Escuela during creation — threaded through to
  // the "Generar clases" modal, which is what now actually generates.
  coordinator?: CoordinatorInput;
  // Groups already persisted to the school (as of the last load) — lets the
  // modal compute which of `groups` are new before persisting them.
  existingGroups: string[];
  refreshClasses: () => Promise<void>;
  // Reloads the full persisted school state after a successful generation
  // (e.g. licensedPrograms, groups) — mirrors the old Confirmación flow.
  onGenerated: () => Promise<void> | void;
  // Creation-flow handoff: when the school was just created with an upfront
  // grid selection, the parent raises this flag so the confirmation modal
  // opens by itself. Acknowledged (and cleared parent-side) via
  // onAutoOpenGenerateHandled whether or not the modal actually opened.
  autoOpenGenerate?: boolean;
  onAutoOpenGenerateHandled?: () => void;
}

const { Option } = Select;

const ClassesTab: React.FC<ClassesTabProps> = ({
  classes,
  setClasses,
  editingClassId,
  setEditingClassId,
  editingClass,
  setEditingClass,
  schoolAbbreviation,
  schoolId,
  campuses = [],
  groups,
  schoolPrograms,
  licensedPrograms,
  generationSelections,
  setGenerationSelections,
  setGroups,
  initialGridSelection,
  initialGridGroups,
  schoolProgramIds,
  coordinator,
  existingGroups,
  refreshClasses,
  onGenerated,
  autoOpenGenerate = false,
  onAutoOpenGenerateHandled,
}) => {
  const { grades } = useGrades();
  const { programs } = usePrograms();
  const { notify } = useNotification();
  const [campusId, setCampusId] = useState<number>(ALL_CAMPUSES);
  const [moveTargetClass, setMoveTargetClass] = useState<ClassEntity | null>(
    null
  );
  const [searchText, setSearchText] = useState("");
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState("generation");
  const [programsDrawerClassId, setProgramsDrawerClassId] = useState<
    number | null
  >(null);
  const [teacherScopeDrawerClassId, setTeacherScopeDrawerClassId] = useState<
    number | null
  >(null);
  const [guideModalOpen, setGuideModalOpen] = useState(false);

  // After a successful generation, land on the management sub-tab so the
  // newly created classes are immediately visible.
  const handleGenerated = async () => {
    await onGenerated();
    setActiveSubTab("management");
  };

  // schoolPrograms reflects the school's licensed programs, but it can lag
  // behind (e.g. right after generation, before a reload completes). Fall
  // back to the global programs list so a class with a valid program_id
  // always shows its title instead of "Sin asignar".
  const resolveProgramTitle = (programId: number): string | undefined =>
    schoolPrograms.find((p) => p.id === programId)?.title ??
    programs.find((p) => p.id === programId)?.title;

  // Función para generar el código de clase automáticamente.
  // Mirrored by the backend-authoritative `buildCustomCode` in
  // emmi-backend/src/classroom/class-generator.utils.ts — the backend is the
  // source of truth for persisted codes; this copy only drives the transient
  // single-add preview below and must be kept in sync with that format.
  const generateClassCode = (
    gradeId: number,
    programId: number,
    classId?: string
  ): string => {
    const grade = grades.find((g) => g.id === gradeId);
    const programTitle = resolveProgramTitle(programId);

    if (!grade || !programTitle || !schoolAbbreviation) {
      return "Completar campos requeridos";
    }

    const gradeCode = grade.code || "";
    const programCode =
      programs.find((p) => p.id === programId)?.program_code || "";

    // Construir el código base
    let code = `${schoolAbbreviation}_${gradeCode}_${programCode}`;

    // Agregar el ID de clase si existe y no está vacío
    if (classId && classId.trim() !== "") {
      // Convertir espacios a guiones bajos y limpiar caracteres especiales
      const cleanClassId = classId
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^a-zA-Z0-9_]/g, "");
      code += `_${cleanClassId}`;
    }

    return code;
  };

  // Post-collapse group code: ABBR_GRADE_GROUP, no program token (mirrors
  // buildGroupCustomCode in the backend collapse tooling — one code per
  // GROUP, its materias live in the join rows).
  const generateGroupCode = (gradeId: number, classId?: string): string => {
    const gradeCode = grades.find((g) => g.id === gradeId)?.code || "";
    const cleanClassId = (classId || "")
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^a-zA-Z0-9_]/g, "");
    if (!schoolAbbreviation || !gradeCode || !cleanClassId) {
      return "Completar campos requeridos";
    }
    return `${schoolAbbreviation}_${gradeCode}_${cleanClassId}`;
  };

  const handleAddClass = () => {
    const newClass: ClassEntity = {
      id: Date.now(), // ID temporal
      grade_id: 0,
      program_id: 0,
      program_ids: [],
      class_id: "",
      class_code: "Completar campos requeridos",
      custom_id: "",
    };
    setClasses([newClass, ...classes]);
    setEditingClassId(newClass.id);
    setEditingClass(newClass);
  };

  const handleEditClass = (classItem: ClassEntity) => {
    setEditingClassId(classItem.id);
    setEditingClass({ ...classItem });
  };

  const handleSaveClass = async () => {
    if (!editingClass || !schoolId) {
      notify(
        "error",
        "Error",
        "Faltan datos requeridos para guardar la clase."
      );
      return;
    }

    const isNewClass = !!editingClass.id && editingClass.id >= TEMP_ID_FLOOR;
    const programIds = Array.from(
      new Set((editingClass.program_ids ?? []).map(Number))
    );

    if (!editingClass.grade_id) {
      notify("error", "Error", "Debe seleccionar un grado.");
      return;
    }
    // A group is created with its materia set; an existing group manages
    // materias in the Programas drawer instead.
    if (isNewClass && programIds.length === 0) {
      notify("error", "Error", "Debe seleccionar al menos una asignatura.");
      return;
    }
    if (!isNewClass && !editingClass.program_id) {
      notify("error", "Error", "Debe seleccionar un programa.");
      return;
    }

    // Validar que el grupo sea obligatorio
    if (!editingClass.class_id || editingClass.class_id.trim() === "") {
      notify(
        "error",
        "Error",
        "Debe seleccionar un grupo. Por favor, complete este campo."
      );
      return;
    }

    // One active class per (school, grade, group) — same rule the backend
    // enforces with 409; checked here first for immediate feedback.
    const trimmedClassId = editingClass.class_id.trim();
    const isDuplicate = classes.some(
      (existingClass) =>
        existingClass.id !== editingClass.id &&
        !existingClass.deleted_at &&
        existingClass.grade_id === editingClass.grade_id &&
        (existingClass.class_id ?? existingClass.custom_id)
          ?.trim()
          ?.toLowerCase() === trimmedClassId.toLowerCase()
    );

    if (isDuplicate) {
      const grade = grades.find((g) => g.id === editingClass.grade_id);
      notify(
        "error",
        "Error",
        `Ya existe una clase activa para el grado "${grade?.grade}" y grupo "${trimmedClassId}". No se pueden crear grupos duplicados.`
      );
      return;
    }

    try {
      // classroom-group-model S4: the API no longer takes a single seed
      // program. A group's materias travel in `program_ids` on creation and
      // through the Programas drawer afterwards.
      const classData = {
        custom_code: isNewClass
          ? generateGroupCode(editingClass.grade_id, editingClass.class_id)
          : editingClass.class_code ||
            generateGroupCode(editingClass.grade_id, editingClass.class_id),
        custom_id: editingClass.class_id || undefined,
        school_id: schoolId,
        grade_id: editingClass.grade_id,
      };

      let savedClass: ClassroomEntity;
      if (isNewClass) {
        const createData: CreateClassroomDto = {
          ...classData,
          program_ids: programIds,
        };
        savedClass = await createClassroom(createData);

        // Actualizar la lista con la clase creada
        const updatedClasses = classes.map((c) =>
          c.id === editingClass.id
            ? {
                ...editingClass,
                id: savedClass.id,
                custom_code: savedClass.custom_code,
                class_code: savedClass.custom_code,
                custom_id: savedClass.custom_id,
                school_id: savedClass.school_id,
              }
            : c
        );
        setClasses(updatedClasses);
        notify("success", "Éxito", "Clase creada correctamente.");
        // The local temp row only carries the picked ids — the table's
        // Materias tags come from the API's virtual per-program rows, so the
        // server truth must be refetched or the new row renders "Sin asignar".
        await refreshClasses();
      } else {
        // Es una clase existente
        const updateData: UpdateClassroomDto = classData;
        savedClass = await updateClassroom(editingClass.id, updateData);

        // Actualizar la lista con la clase modificada
        const updatedClasses = classes.map((c) =>
          c.id === editingClass.id
            ? {
                ...editingClass,
                custom_code: savedClass.custom_code,
                class_code: savedClass.custom_code,
                custom_id: savedClass.custom_id,
                school_id: savedClass.school_id,
              }
            : c
        );
        setClasses(updatedClasses);
        notify("success", "Éxito", "Clase actualizada correctamente.");
      }

      setEditingClassId(null);
      setEditingClass(null);
    } catch (error: any) {
      console.error("Error saving class:", error);
      // 409 (grupo duplicado) y validaciones del backend llegan con mensaje propio.
      notify(
        "error",
        "Error",
        error?.response?.data?.message ??
          "Ocurrió un error al guardar la clase."
      );
    }
  };

  const handleCancelEdit = () => {
    // Si estamos cancelando la edición de una clase nueva (ID temporal), eliminarla de la lista
    if (editingClass && editingClass.id > 1000000) {
      const updatedClasses = classes.filter((c) => c.id !== editingClass.id);
      setClasses(updatedClasses);
    }

    setEditingClassId(null);
    setEditingClass(null);
  };

  const handleDeleteClass = async (id: number) => {
    try {
      // Si el ID es temporal (mayor a 1000000), solo remover de la lista local
      if (id > 1000000) {
        const updatedClasses = classes.filter((c) => c.id !== id);
        setClasses(updatedClasses);
        notify("info", "Información", "Clase removida de la lista.");
        return;
      }

      // Si es una clase existente en el backend, eliminarla
      await deleteClassroom(id);
      const updatedClasses = classes.filter((c) => c.id !== id);
      setClasses(updatedClasses);
      notify("success", "Éxito", "Clase eliminada correctamente.");
    } catch (error: any) {
      console.error("Error deleting class:", error);
      notify("error", "Error", "Ocurrió un error al eliminar la clase.");
    }
  };

  // Attachable materias for a grade, split into unambiguous programs (one
  // per subject — the pre-fill) and same-subject level collisions (e.g.
  // básico/avanzado) that demand an explicit pick.
  const groupGradePrograms = (gradeId: number) => {
    const licensedIds = new Set(licensedPrograms.map((p) => p.id));
    const attachable = programs.filter(
      (p: any) =>
        licensedIds.has(p.id) &&
        (p.grade?.id ?? p.grade_id) === gradeId &&
        p.subject?.code !== "SEL" &&
        p.subject?.name !== "Socioemocional"
    );
    const bySubject = new Map<number, any[]>();
    attachable.forEach((p: any) => {
      const key = p.subject?.id ?? p.subject_id ?? p.id;
      bySubject.set(key, [...(bySubject.get(key) ?? []), p]);
    });
    const groups = Array.from(bySubject.values());
    return {
      attachable,
      prefillIds: groups
        .filter((group) => group.length === 1)
        .map((group) => group[0].id),
      collidingSubjects: groups
        .filter((group) => group.length > 1)
        .map((group) => group[0].subject?.name)
        .filter(Boolean) as string[],
    };
  };

  const handleClassFieldChange = (field: keyof ClassEntity, value: any) => {
    if (editingClass) {
      const updatedClass = { ...editingClass, [field]: value };

      // Sincronizar class_id con custom_id
      if (field === "class_id") {
        updatedClass.custom_id = value;
      }

      // Materias are grade-scoped. Picking a grade PRE-FILLS every
      // unambiguous licensed materia of that grade — the common case is a
      // class with all of them; the admin prunes only when they really want
      // less. Level collisions stay out of the pre-fill (see the warning in
      // the Materias cell).
      if (field === "grade_id" && updatedClass.id >= TEMP_ID_FLOOR) {
        updatedClass.program_ids = groupGradePrograms(value).prefillIds;
      }

      // Generar automáticamente el código de clase cuando cambien los campos relevantes
      if (
        field === "grade_id" ||
        field === "program_id" ||
        field === "class_id"
      ) {
        updatedClass.class_code = generateGroupCode(
          updatedClass.grade_id,
          updatedClass.class_id
        );
      }

      setEditingClass(updatedClass);
    }
  };

  const showCampusFilter = hasMultipleCampuses(campuses);
  const showCampusColumn = showCampusFilter && campusId === ALL_CAMPUSES;

  // Texto de búsqueda sobre custom_code/custom_id + plantel (client-side: con
  // planteles el dataset es el ÁRBOL entero, no una sola escuela).
  const searchedClasses = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const byCampus =
      campusId === ALL_CAMPUSES
        ? classes
        : classes.filter((c) => c.school_id === campusId);
    if (!query) return byCampus;
    return byCampus.filter((c) => {
      const code = (c.custom_code || c.class_code || "").toLowerCase();
      const customId = (c.custom_id || c.class_id || "").toLowerCase();
      return code.includes(query) || customId.includes(query);
    });
  }, [classes, searchText, campusId]);

  // classroom-group-model: the API exposes one virtual row per licensed
  // program of a group (same classroom id repeated N times). This table shows
  // the GROUP, so the virtual rows collapse back into one per classroom; the
  // full program set feeds the Materias column and its filter. Per-materia
  // management lives behind the "Programas" drawer. Each materia's title
  // comes from the row's own `program` when present: the local lookups
  // filter out soft-deleted programs, but a group may legitimately carry a
  // join row to a retired program and should still name it.
  const groupedClasses = useMemo(() => {
    type ProgramEntry = { id: number; title: string };
    const rowTitle = (c: ClassEntity): string =>
      (c as ClassEntity & { program?: { title?: string } }).program?.title ??
      resolveProgramTitle(c.program_id) ??
      "Sin asignar";
    const byId = new Map<
      number,
      ClassEntity & { program_ids: number[]; programEntries: ProgramEntry[] }
    >();
    for (const c of searchedClasses) {
      const existing = byId.get(c.id);
      if (existing) {
        if (!existing.program_ids.includes(c.program_id)) {
          existing.program_ids.push(c.program_id);
          existing.programEntries.push({
            id: c.program_id,
            title: rowTitle(c),
          });
        }
      } else {
        byId.set(c.id, {
          ...c,
          program_ids: [c.program_id],
          programEntries: [{ id: c.program_id, title: rowTitle(c) }],
        });
      }
    }
    return Array.from(byId.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchedClasses, schoolPrograms, programs]);

  const gradeFilters = useMemo(
    () => grades.map((g) => ({ text: g.grade, value: g.id })),
    [grades]
  );
  const programFilters = useMemo(
    () => schoolPrograms.map((p) => ({ text: p.title, value: p.id })),
    [schoolPrograms]
  );

  // Definir las columnas para la tabla de clases
  const classColumns = [
    // SCRUM-916 (R3.11): sólo en la vista "Todos los planteles" de una escuela
    // con más de uno; con un plantel elegido la columna repetiría el filtro.
    ...(showCampusColumn
      ? [
          {
            title: "Plantel",
            dataIndex: "school_id",
            key: "school_id",
            width: 140,
            render: (schoolIdOfClass: number | undefined) =>
              campuses.find((campus) => campus.id === schoolIdOfClass)?.name ??
              "—",
          },
        ]
      : []),
    {
      title: "Grado",
      dataIndex: "grade_id",
      key: "grade_id",
      filters: gradeFilters,
      onFilter: (value: any, record: ClassEntity) => record.grade_id === value,
      sorter: (a: ClassEntity, b: ClassEntity) => {
        const gradeA = grades.find((g) => g.id === a.grade_id)?.grade || "";
        const gradeB = grades.find((g) => g.id === b.grade_id)?.grade || "";
        return gradeA.localeCompare(gradeB);
      },
      render: (gradeId: number, record: ClassEntity) => {
        if (editingClassId === record.id) {
          return (
            <Select
              value={editingClass?.grade_id || undefined}
              onChange={(value) => handleClassFieldChange("grade_id", value)}
              placeholder="Seleccionar grado"
              style={{ width: "100%" }}
            >
              {grades.map((grade) => (
                <Option key={grade.id} value={grade.id}>
                  {grade.grade}
                </Option>
              ))}
            </Select>
          );
        }
        const grade = grades.find((g) => g.id === gradeId);
        return grade ? grade.grade : "Sin asignar";
      },
    },
    {
      title: "Asignaturas",
      dataIndex: "program_ids",
      key: "program_ids",
      filters: programFilters,
      onFilter: (value: any, record: ClassEntity & { program_ids: number[] }) =>
        record.program_ids.includes(value),
      sorter: (
        a: ClassEntity & { program_ids: number[] },
        b: ClassEntity & { program_ids: number[] }
      ) => b.program_ids.length - a.program_ids.length,
      render: (
        _programIds: number[],
        record: ClassEntity & {
          program_ids: number[];
          programEntries: { id: number; title: string }[];
        }
      ) => {
        // Only a NEW (unsaved) group picks its materias inline; an existing
        // group manages them in the Programas drawer.
        if (editingClassId === record.id && record.id >= TEMP_ID_FLOOR) {
          const { attachable, collidingSubjects } = groupGradePrograms(
            editingClass?.grade_id ?? 0
          );
          return (
            <>
              <Select
                mode="multiple"
                value={editingClass?.program_ids ?? []}
                onChange={(values) =>
                  handleClassFieldChange("program_ids", values)
                }
                placeholder="Seleccionar asignaturas"
                style={{ width: "100%" }}
              >
                {attachable.map((program: any) => (
                  <Option key={program.id} value={program.id}>
                    {program.title}
                  </Option>
                ))}
              </Select>
              {collidingSubjects.length > 0 && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: 8 }}
                  message={`Hay más de un programa para ${collidingSubjects.join(
                    ", "
                  )}; seleccione explícitamente cuál corresponde (puede elegir más de uno).`}
                />
              )}
            </>
          );
        }
        return (
          <Space size={[0, 4]} wrap>
            {record.programEntries.map((entry) => (
              <Tag key={entry.id}>{entry.title}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Grupo *",
      dataIndex: "class_id",
      key: "class_id",
      sorter: (a: ClassEntity, b: ClassEntity) =>
        (a.class_id || "").localeCompare(b.class_id || ""),
      render: (classId: string, record: ClassEntity) => {
        if (editingClassId === record.id) {
          return (
            <Select
              value={editingClass?.class_id || undefined}
              onChange={(value) => handleClassFieldChange("class_id", value)}
              placeholder="Seleccionar grupo"
              style={{
                width: "100%",
                borderColor:
                  !editingClass?.class_id ||
                  editingClass.class_id?.trim() === ""
                    ? "#ff4d4f"
                    : undefined,
              }}
              allowClear
            >
              {groups.map((group) => (
                <Option key={group} value={group}>
                  {group}
                </Option>
              ))}
            </Select>
          );
        }
        return classId || "Sin asignar";
      },
    },
    {
      title: "Código de Clase",
      dataIndex: "custom_code",
      key: "custom_code",
      sorter: (a: ClassEntity, b: ClassEntity) =>
        (a.custom_code || a.class_code || "").localeCompare(
          b.custom_code || b.class_code || ""
        ),
      render: (customCode: string, record: ClassEntity) => {
        // Mientras se edita una clase nueva (sin guardar) aún no hay custom_code
        // backend-autoritativo; se muestra un preview local solo para ese caso.
        if (editingClassId === record.id && editingClass && !record.custom_code) {
          const previewCode = generateGroupCode(
            editingClass.grade_id,
            editingClass.class_id
          );
          return (
            <span style={{ fontStyle: "italic", color: "var(--bo-text-secondary)" }}>
              {previewCode}
            </span>
          );
        }

        // Código autoritativo del backend — sin recomputar en el cliente.
        const displayCode = record.custom_code || record.class_code || "Sin asignar";

        return (
          <span style={{ fontStyle: "italic", color: "var(--bo-text-secondary)" }}>
            {displayCode}
          </span>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      render: (_: any, record: ClassEntity) => {
        if (editingClassId === record.id) {
          return (
            <Space>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSaveClass}
                size="small"
              >
                Guardar
              </Button>
              <Button
                icon={<CloseOutlined />}
                onClick={handleCancelEdit}
                size="small"
              >
                Cancelar
              </Button>
            </Space>
          );
        }
        return (
          <Space>
            <Button
              type="default"
              icon={<UnorderedListOutlined />}
              onClick={() => setProgramsDrawerClassId(record.id)}
              disabled={record.id >= TEMP_ID_FLOOR}
              size="small"
            >
              Asignaturas
            </Button>
            <Button
              type="default"
              icon={<TeamOutlined />}
              onClick={() => setTeacherScopeDrawerClassId(record.id)}
              disabled={record.id >= TEMP_ID_FLOOR}
              size="small"
            >
              Profesores
            </Button>
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => handleEditClass(record)}
              size="small"
            >
              Editar
            </Button>
            {showCampusFilter && (
              <Button
                type="default"
                icon={<SwapOutlined />}
                onClick={() => setMoveTargetClass(record)}
                disabled={record.id >= TEMP_ID_FLOOR}
                size="small"
              >
                Mover a plantel
              </Button>
            )}
            <Popconfirm
              title="¿Estás seguro de eliminar esta clase?"
              onConfirm={() => handleDeleteClass(record.id)}
              okText="Sí"
              cancelText="No"
            >
              <Button
                type="default"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                Eliminar
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  // Both panes stay mounted (forceRender) so the generation grid's transient
  // selection and the table's edit state survive sub-tab switches.
  // Same delta the grid's summary shows: with nothing NEW to create the
  // button stays disabled — an all-existing selection would only re-confirm
  // classes the backend would skip anyway (empty selection included).
  const generationDelta = countGenerationDelta(generationSelections, classes);

  // Creation-flow handoff: open the confirmation modal by itself right after
  // the school was created with an upfront selection. Keyed on the flag only
  // — later selection changes must never re-trigger it.
  useEffect(() => {
    if (!autoOpenGenerate) return;
    if (schoolId && generationDelta.newCount > 0) {
      setGenerateModalOpen(true);
    }
    onAutoOpenGenerateHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenGenerate]);

  const generationPane = (
    <div className="generation-grid-section">
      {/* Keyed per school: the grid seeds its internal selection once per
          mount, so switching schools (back/forward between /schools/:id
          entries) must remount it or the previous school's picks survive. */}
      <SectionGradeProgramGrid
        key={schoolId ?? "creation"}
        setGenerationSelections={setGenerationSelections}
        groups={groups}
        setGroups={setGroups}
        initialSelection={initialGridSelection}
        initialGroupsByGrade={initialGridGroups}
        existingClasses={classes}
        existingGroups={existingGroups}
        schoolProgramIds={schoolProgramIds}
      />
      <Button
        type="primary"
        onClick={() => setGenerateModalOpen(true)}
        disabled={generationDelta.newCount === 0}
        style={{ marginTop: 16 }}
      >
        Generar clases
      </Button>
    </div>
  );

  const managementPane = (
    <div className="classes-management-section">
      <div className="classes-header" style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
        <Button
          type="default"
          icon={<QuestionCircleOutlined />}
          onClick={() => setGuideModalOpen(true)}
        >
          ¿Cómo funcionan las clases?
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddClass}
          className="add-class-btn"
          disabled={licensedPrograms.length === 0}
        >
          Agregar Clase
        </Button>
      </div>

      {licensedPrograms.length === 0 && (
        <Alert
          message="Sin programas asignados"
          description="Esta escuela no tiene programas asignados. Para crear clases, primero debe asignar programas a la escuela en la pestaña 'Escuela'."
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Space style={{ marginBottom: 16 }} wrap>
        <Input.Search
          placeholder="Buscar por código o grupo"
          allowClear
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 320 }}
        />
        {showCampusFilter && (
          <Select
            style={{ width: 240 }}
            aria-label="Plantel"
            value={campusId}
            onChange={setCampusId}
            options={campusOptions(campuses)}
          />
        )}
      </Space>

      <Table
        columns={classColumns}
        dataSource={groupedClasses}
        rowKey="id"
        pagination={false}
        className="classes-table"
        locale={{
          emptyText:
            licensedPrograms.length === 0
              ? "No se pueden crear clases sin programas asignados a la escuela."
              : "No hay clases registradas. Haz clic en 'Agregar Clase' para comenzar.",
        }}
      />
    </div>
  );

  return (
    <div className="classes-container">
      <Tabs
        activeKey={activeSubTab}
        onChange={setActiveSubTab}
        items={[
          {
            key: "generation",
            label: "Generación de Clases",
            forceRender: true,
            children: generationPane,
          },
          {
            key: "management",
            label: "Gestión de Clases",
            forceRender: true,
            children: managementPane,
          },
        ]}
      />

      <ClassesGuideModal
        open={guideModalOpen}
        onClose={() => setGuideModalOpen(false)}
      />

      {/* SCRUM-916: mover una clase entera a otro plantel del árbol */}
      {moveTargetClass && schoolId && (
        <MoveToCampusModal
          open
          onClose={() => setMoveTargetClass(null)}
          rootSchoolId={schoolId}
          currentSchoolId={moveTargetClass.school_id ?? schoolId}
          target={{
            kind: "classroom",
            id: moveTargetClass.id,
            label:
              moveTargetClass.custom_code ||
              moveTargetClass.class_code ||
              `Clase ${moveTargetClass.id}`,
          }}
          onMoved={() => {
            setMoveTargetClass(null);
            refreshClasses();
          }}
        />
      )}

      {schoolId && (
        <GenerarClasesModal
          open={generateModalOpen}
          onClose={() => setGenerateModalOpen(false)}
          schoolId={schoolId}
          selections={generationSelections}
          coordinator={coordinator}
          groups={groups}
          existingGroups={existingGroups}
          refreshClasses={refreshClasses}
          onGenerated={handleGenerated}
        />
      )}

      {schoolId && programsDrawerClassId && (
        <ClassProgramsDrawer
          open={programsDrawerClassId !== null}
          onClose={() => setProgramsDrawerClassId(null)}
          schoolId={schoolId}
          classroomId={programsDrawerClassId}
          classroomLabel={
            classes.find((c) => c.id === programsDrawerClassId)
              ?.custom_code ?? ""
          }
          gradeId={
            classes.find((c) => c.id === programsDrawerClassId)?.grade_id ??
            null
          }
          licensedProgramIds={licensedPrograms.map((p) => p.id)}
          onProgramsChanged={refreshClasses}
        />
      )}

      {schoolId && teacherScopeDrawerClassId && (
        <ClassTeacherScopeDrawer
          open={teacherScopeDrawerClassId !== null}
          onClose={() => setTeacherScopeDrawerClassId(null)}
          schoolId={schoolId}
          classroomId={teacherScopeDrawerClassId}
          classroomLabel={
            classes.find((c) => c.id === teacherScopeDrawerClassId)
              ?.custom_code ?? ""
          }
        />
      )}
    </div>
  );
};

export default ClassesTab;
